'use client'

import { useMemo, memo } from 'react'
import { NotebookIcon } from '@phosphor-icons/react'
import type { EntryWithPeople } from '@/document'
import { EntryGroup } from './EntryGroup'

interface EntriesProps {
  tasks: EntryWithPeople[]
  journals: EntryWithPeople[]
  movies: EntryWithPeople[]
  books: EntryWithPeople[]
  artists: EntryWithPeople[]
  labels: EntryWithPeople[]
  releases: EntryWithPeople[]
  lastInteraction: string | null
}

export type TimelineEntry =
  | { type: 'task'; data: EntryWithPeople; date: Date }
  | { type: 'journal'; data: EntryWithPeople; date: Date }
  | { type: 'movie'; data: EntryWithPeople; date: Date }
  | { type: 'book'; data: EntryWithPeople; date: Date }
  | { type: 'artist'; data: EntryWithPeople; date: Date }
  | { type: 'label'; data: EntryWithPeople; date: Date }
  | { type: 'release'; data: EntryWithPeople; date: Date }

export const EntriesTimeline = memo(function EntriesTimeline({
  entries,
}: {
  entries: EntriesProps
}) {
  // Combine and sort entries by date
  const allEntries: TimelineEntry[] = useMemo(() => {
    const combined: TimelineEntry[] = [
      ...entries.tasks.map((t) => ({
        type: 'task' as const,
        data: t,
        date: new Date(t.createdAt),
      })),
      ...entries.journals.map((j) => ({
        type: 'journal' as const,
        data: j,
        date: new Date(j.createdAt),
      })),
      ...entries.movies.map((m) => ({
        type: 'movie' as const,
        data: m,
        date: new Date(m.createdAt),
      })),
      ...entries.books.map((b) => ({
        type: 'book' as const,
        data: b,
        date: new Date(b.createdAt),
      })),
      ...entries.artists.map((a) => ({
        type: 'artist' as const,
        data: a,
        date: new Date(a.createdAt),
      })),
      ...entries.labels.map((l) => ({
        type: 'label' as const,
        data: l,
        date: new Date(l.createdAt),
      })),
      ...entries.releases.map((r) => ({
        type: 'release' as const,
        data: r,
        date: new Date(r.createdAt),
      })),
    ]
    return combined.sort((a, b) => b.date.getTime() - a.date.getTime())
  }, [entries])

  // Group entries by time period
  const groupedEntries = useMemo(() => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

    const groups: {
      thisWeek: TimelineEntry[]
      thisMonth: TimelineEntry[]
      older: TimelineEntry[]
    } = {
      thisWeek: [],
      thisMonth: [],
      older: [],
    }

    allEntries.forEach((entry) => {
      if (entry.date >= weekAgo) {
        groups.thisWeek.push(entry)
      } else if (entry.date >= monthAgo) {
        groups.thisMonth.push(entry)
      } else {
        groups.older.push(entry)
      }
    })

    return groups
  }, [allEntries])

  if (allEntries.length === 0) {
    return (
      <div className="flex items-start gap-4 py-8">
        <div className="bg-muted/50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <NotebookIcon className="text-muted-foreground h-5 w-5" />
        </div>
        <div>
          <p className="text-foreground text-sm font-medium">
            No entries with this person yet
          </p>
          <p className="text-muted-foreground mt-1 max-w-xs text-xs">
            Tag them in a journal, task, or media entry to see your interactions
            here
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {groupedEntries.thisWeek.length > 0 && (
        <EntryGroup title="This Week" entries={groupedEntries.thisWeek} />
      )}
      {groupedEntries.thisMonth.length > 0 && (
        <EntryGroup title="This Month" entries={groupedEntries.thisMonth} />
      )}
      {groupedEntries.older.length > 0 && (
        <EntryGroup title="Older" entries={groupedEntries.older} />
      )}
    </div>
  )
})
